import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { quotationsAPI, venuesAPI, leadsAPI, invoicesAPI } from '../services/api';
import { formatCurrency, formatDate } from '../utils/currency';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { generateQuotationPDF } from '../utils/quotationPdfGenerator';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import {
  Plus, Search, FileText, Edit, Trash2, Send, Check, X, Download,
  MessageCircle, ShieldOff, Building2, RefreshCw, ChevronRight, Lock,
  CheckCircle, XCircle, Repeat, Settings, Camera, Image as ImageIcon, RectangleHorizontal, RectangleVertical
} from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  sent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  viewed: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  accepted: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  expired: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  converted: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
};

const emptySpace = () => ({ space_id: '', space_name: '', space_type: '', quantity: 1, unit_price: 0, total_price: 0, images: [] });
const emptyAmenity = () => ({ amenity_id: '', amenity_name: '', category: '', quantity: 1, unit: 'nos', unit_price: 0, days: 1, total_price: 0, images: [] });

const defaultValidUntil = () => {
  const d = new Date();
  d.setDate(d.getDate() + 15);
  return d.toISOString().split('T')[0];
};

const ModuleDisabledScreen = () => (
  <div className="min-h-[70vh] flex items-center justify-center p-4">
    <Card className="max-w-lg w-full border-2 border-dashed border-orange-200 dark:border-orange-900">
      <CardContent className="p-8 text-center space-y-4">
        <div className="mx-auto w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
          <Lock className="w-8 h-8 text-orange-600 dark:text-orange-400" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-foreground">
          Quotations Module is Disabled
        </h2>
        <p className="text-sm text-gray-600 dark:text-muted-foreground">
          This feature is currently not activated for your account.
          Please ask the Software Developer / MyVenueMate Admin to enable the <b>Quotations</b> module from the Master Control Panel.
        </p>
        <div className="text-xs text-gray-500 dark:text-muted-foreground pt-2 border-t dark:border-border">
          Contact: <span className="font-medium">support@myvenuemate.in</span>
        </div>
      </CardContent>
    </Card>
  </div>
);

// Photo picker for a line item — supports up to 2 photos, each with its own
// orientation toggle (landscape/portrait ATM card size on the PDF).
// Props:
//   images: [{ url, orientation }] array (length 0..2)
//   onChange(nextImages) receives the updated array
const MAX_IMAGES_PER_ITEM = 2;

const ItemPhotoSlot = ({ image, slotIndex, onChange, onRemove, testIdPrefix }) => {
  const [busy, setBusy] = React.useState(false);
  const fileRef = React.useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      window.alert('Image must be under 5MB');
      return;
    }
    try {
      setBusy(true);
      const res = await quotationsAPI.uploadItemImage(file);
      onChange({ url: res.path, orientation: image?.orientation || 'landscape' });
    } catch (err) {
      window.alert(err.message || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const hasImage = !!image?.url;
  const previewUrl = hasImage ? quotationsAPI.getItemImageUrl(image.url) : null;
  const orientation = image?.orientation || 'landscape';

  return (
    <div className="flex items-center gap-1.5">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleUpload}
        data-testid={`${testIdPrefix}-slot${slotIndex}-file`}
      />
      {hasImage ? (
        <div className="relative w-10 h-10 rounded border dark:border-border overflow-hidden bg-gray-50 dark:bg-secondary">
          <img src={previewUrl} alt={`item-${slotIndex}`} className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={onRemove}
            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] leading-none flex items-center justify-center shadow"
            data-testid={`${testIdPrefix}-slot${slotIndex}-remove`}
            aria-label="remove photo"
          >
            ×
          </button>
        </div>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-9 px-2"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          data-testid={`${testIdPrefix}-slot${slotIndex}-upload`}
          title={`Attach photo ${slotIndex + 1}`}
        >
          {busy ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
        </Button>
      )}
      {hasImage && (
        <div className="flex border rounded overflow-hidden dark:border-border">
          <button
            type="button"
            className={`px-1.5 py-1 ${orientation === 'landscape' ? 'bg-teal-600 text-white' : 'bg-white dark:bg-card text-gray-600 dark:text-muted-foreground'}`}
            onClick={() => onChange({ ...image, orientation: 'landscape' })}
            data-testid={`${testIdPrefix}-slot${slotIndex}-orientation-landscape`}
            title="Landscape (ATM card)"
          >
            <RectangleHorizontal className="w-3 h-3" />
          </button>
          <button
            type="button"
            className={`px-1.5 py-1 ${orientation === 'portrait' ? 'bg-teal-600 text-white' : 'bg-white dark:bg-card text-gray-600 dark:text-muted-foreground'}`}
            onClick={() => onChange({ ...image, orientation: 'portrait' })}
            data-testid={`${testIdPrefix}-slot${slotIndex}-orientation-portrait`}
            title="Portrait"
          >
            <RectangleVertical className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

const ItemPhotosPicker = ({ images, onChange, testIdPrefix }) => {
  const list = Array.isArray(images) ? images : [];
  const slots = [];
  for (let i = 0; i < MAX_IMAGES_PER_ITEM; i += 1) {
    slots.push(list[i] || null);
  }

  const updateSlot = (slotIndex, nextImg) => {
    const next = [...list];
    if (nextImg) {
      next[slotIndex] = nextImg;
    } else {
      // removal — drop the slot and compact the array so indexes stay packed
      next.splice(slotIndex, 1);
    }
    onChange(next.filter((x) => x && x.url));
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {slots.map((img, idx) => (
        <ItemPhotoSlot
          key={idx}
          image={img}
          slotIndex={idx}
          onChange={(v) => updateSlot(idx, v)}
          onRemove={() => updateSlot(idx, null)}
          testIdPrefix={testIdPrefix}
        />
      ))}
      <span className="text-[10px] text-gray-400 dark:text-muted-foreground">
        up to {MAX_IMAGES_PER_ITEM} photos • ATM card size on PDF
      </span>
    </div>
  );
};

const Quotations = () => {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Access guard (client-side): use enabledModules from currentUser as first filter,
  // backend will still enforce on every API call.
  // Master admin always has access; other users must have 'quotations' in enabledModules.
  const enabledModules = currentUser?.enabledModules;
  const moduleEnabled = !!currentUser?.isMaster
    || (Array.isArray(enabledModules) && enabledModules.includes('quotations'));

  const [quotations, setQuotations] = useState([]);
  const [venues, setVenues] = useState([]);
  const [leads, setLeads] = useState([]);
  const [invoiceSettings, setInvoiceSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    lead_id: '',
    venue_id: '',
    venue_name: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    event_type: '',
    event_date_from: '',
    event_date_to: '',
    guest_count: '',
    spaces: [emptySpace()],
    amenities: [],
    discount_percent: 0,
    discount_amount: 0,
    gst_type: 'exclusive',
    gst_percent: 18,
    valid_until: defaultValidUntil(),
    terms_and_conditions: '',
    notes: ''
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [q, v] = await Promise.all([
        quotationsAPI.getAll(),
        // Degrade gracefully for staff without venues permission
        venuesAPI.getAll().catch(() => [])
      ]);
      setQuotations(q.quotations || []);
      setVenues(v || []);
      // Fire-and-forget loads
      leadsAPI.getAll().then(setLeads).catch(() => {});
      invoicesAPI.getSettings().then(setInvoiceSettings).catch(() => setInvoiceSettings(null));
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (moduleEnabled) loadData();
  }, [moduleEnabled, loadData]);

  const resetForm = () => {
    setForm({
      lead_id: '', venue_id: '', venue_name: '',
      client_name: '', client_email: '', client_phone: '',
      event_type: '', event_date_from: '', event_date_to: '', guest_count: '',
      spaces: [emptySpace()], amenities: [],
      discount_percent: 0, discount_amount: 0,
      gst_type: 'exclusive', gst_percent: 18,
      valid_until: defaultValidUntil(), terms_and_conditions: '', notes: ''
    });
    setEditingId(null);
  };

  const openCreate = () => { resetForm(); setFormOpen(true); };

  const openEdit = async (q) => {
    try {
      const full = await quotationsAPI.get(q.id);
      setForm({
        lead_id: full.lead_id || '',
        venue_id: full.venue_id || '',
        venue_name: full.venue_name || '',
        client_name: full.client_name || '',
        client_email: full.client_email || '',
        client_phone: full.client_phone || '',
        event_type: full.event_type || '',
        event_date_from: full.event_date_from ? full.event_date_from.split('T')[0] : '',
        event_date_to: full.event_date_to ? full.event_date_to.split('T')[0] : '',
        guest_count: full.guest_count || '',
        spaces: full.spaces && full.spaces.length ? full.spaces : [emptySpace()],
        amenities: full.amenities || [],
        discount_percent: full.discount_percent || 0,
        discount_amount: full.discount_amount || 0,
        gst_type: full.gst_type || 'exclusive',
        gst_percent: full.gst_percent ?? 18,
        valid_until: full.valid_until ? full.valid_until.split('T')[0] : defaultValidUntil(),
        terms_and_conditions: full.terms_and_conditions || '',
        notes: full.notes || ''
      });
      setEditingId(q.id);
      setFormOpen(true);
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  // Totals
  const totals = useMemo(() => {
    const spacesTotal = form.spaces.reduce((s, sp) => s + (Number(sp.quantity) || 0) * (Number(sp.unit_price) || 0), 0);
    const amenTotal = form.amenities.reduce((s, a) => s + (Number(a.quantity) || 0) * (Number(a.unit_price) || 0) * (Number(a.days) || 1), 0);
    const subtotal = spacesTotal + amenTotal;
    const discAmt = form.discount_percent ? (subtotal * Number(form.discount_percent) / 100) : Number(form.discount_amount || 0);
    const net = subtotal - discAmt;
    const gstPct = Number(form.gst_percent) || 0;
    let gstAmount = 0; let grand = net;
    if (form.gst_type === 'exclusive') {
      gstAmount = net * gstPct / 100;
      grand = net + gstAmount;
    } else {
      gstAmount = net * gstPct / (100 + gstPct);
      grand = net;
    }
    return {
      spacesTotal, amenTotal, subtotal,
      discount: discAmt, net,
      gst: gstAmount, grand
    };
  }, [form]);

  // Line item mutators
  const updateSpace = (idx, patch) => {
    setForm((f) => {
      const next = [...f.spaces];
      const merged = { ...next[idx], ...patch };
      merged.total_price = (Number(merged.quantity) || 0) * (Number(merged.unit_price) || 0);
      next[idx] = merged;
      return { ...f, spaces: next };
    });
  };
  const addSpace = () => setForm((f) => ({ ...f, spaces: [...f.spaces, emptySpace()] }));
  const removeSpace = (idx) => setForm((f) => ({ ...f, spaces: f.spaces.filter((_, i) => i !== idx) }));

  const updateAmenity = (idx, patch) => {
    setForm((f) => {
      const next = [...f.amenities];
      const merged = { ...next[idx], ...patch };
      merged.total_price = (Number(merged.quantity) || 0) * (Number(merged.unit_price) || 0) * (Number(merged.days) || 1);
      next[idx] = merged;
      return { ...f, amenities: next };
    });
  };
  const addAmenity = () => setForm((f) => ({ ...f, amenities: [...f.amenities, emptyAmenity()] }));
  const removeAmenity = (idx) => setForm((f) => ({ ...f, amenities: f.amenities.filter((_, i) => i !== idx) }));

  // Venue change hydrates venue_name
  const onVenueChange = (venueId) => {
    const v = venues.find((x) => x.id === venueId);
    setForm((f) => ({ ...f, venue_id: venueId, venue_name: v?.name || '' }));
  };

  // Lead change auto-fills client & event details from the selected lead
  const onLeadChange = (leadId) => {
    if (leadId === 'none' || !leadId) {
      setForm((f) => ({ ...f, lead_id: '' }));
      return;
    }
    const lead = leads.find((l) => l.id === leadId);
    if (!lead) {
      setForm((f) => ({ ...f, lead_id: leadId }));
      return;
    }
    // Leads have varying schemas across the app - handle both bookingDate* and expectedDate
    const dateFrom = (lead.bookingDateFrom || lead.expectedDate || '').split('T')[0] || '';
    const dateTo = (lead.bookingDateTo || lead.expectedDate || '').split('T')[0] || '';
    setForm((f) => ({
      ...f,
      lead_id: leadId,
      client_name: lead.name || f.client_name,
      client_phone: lead.phone || f.client_phone,
      client_email: lead.email || f.client_email,
      event_type: lead.eventType || f.event_type,
      event_date_from: dateFrom || f.event_date_from,
      event_date_to: dateTo || f.event_date_to,
      guest_count: lead.expectedGuests ?? lead.guests ?? f.guest_count,
      notes: f.notes || lead.notes || ''
    }));
    toast({ title: 'Lead loaded', description: `Pre-filled details from ${lead.name}` });
  };

  const handleSave = async () => {
    if (!form.client_name || !form.client_phone || !form.venue_id) {
      toast({ title: 'Missing fields', description: 'Client name, phone, and venue are required', variant: 'destructive' });
      return;
    }
    try {
      const payload = {
        ...form,
        guest_count: form.guest_count ? parseInt(form.guest_count) : null,
        spaces: form.spaces.filter((s) => s.space_name).map((s) => ({
          ...s,
          space_id: s.space_id || s.space_name,
          quantity: Number(s.quantity) || 1,
          unit_price: Number(s.unit_price) || 0,
          total_price: (Number(s.quantity) || 0) * (Number(s.unit_price) || 0)
        })),
        amenities: form.amenities.filter((a) => a.amenity_name).map((a) => ({
          ...a,
          amenity_id: a.amenity_id || a.amenity_name,
          quantity: Number(a.quantity) || 1,
          unit_price: Number(a.unit_price) || 0,
          days: Number(a.days) || 1,
          total_price: (Number(a.quantity) || 0) * (Number(a.unit_price) || 0) * (Number(a.days) || 1)
        })),
        discount_percent: Number(form.discount_percent) || 0,
        discount_amount: Number(form.discount_amount) || 0,
        gst_percent: Number(form.gst_percent) || 0,
        valid_until: form.valid_until || null,
        event_date_from: form.event_date_from || null,
        event_date_to: form.event_date_to || null
      };
      if (editingId) {
        await quotationsAPI.update(editingId, payload);
        toast({ title: 'Updated', description: 'Quotation updated' });
      } else {
        await quotationsAPI.create(payload);
        toast({ title: 'Created', description: 'Quotation created' });
      }
      setFormOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleAction = async (action, q) => {
    try {
      if (action === 'send') await quotationsAPI.markSent(q.id);
      if (action === 'accept') await quotationsAPI.accept(q.id);
      if (action === 'reject') await quotationsAPI.reject(q.id);
      if (action === 'convert') await quotationsAPI.convertToBooking(q.id);
      toast({ title: 'Success', description: `Quotation ${action === 'send' ? 'sent' : action + 'ed'}` });
      loadData();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleDownloadPDF = async (q) => {
    try {
      const data = await quotationsAPI.getPdfData(q.id);
      // Backend returns snake_case `invoice_settings`; generator expects camelCase
      await generateQuotationPDF({
        quotation: data.quotation,
        admin: data.admin,
        venue: data.venue,
        invoiceSettings: data.invoice_settings || data.invoiceSettings
      });
      toast({ title: 'Downloaded', description: `${data.filename || 'quotation.pdf'}` });
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleWhatsApp = async (q) => {
    try {
      const { whatsapp_url } = await quotationsAPI.getWhatsAppLink(q.id);
      window.open(whatsapp_url, '_blank');
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await quotationsAPI.delete(deleteTarget.id);
      toast({ title: 'Deleted', description: 'Quotation removed' });
      setDeleteOpen(false);
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const filtered = useMemo(() => {
    let result = quotations;
    if (filterStatus !== 'all') result = result.filter((q) => q.status === filterStatus);
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      result = result.filter((q) =>
        (q.quotation_number || '').toLowerCase().includes(t) ||
        (q.client_name || '').toLowerCase().includes(t) ||
        (q.client_phone || '').toLowerCase().includes(t) ||
        (q.venue_name || '').toLowerCase().includes(t)
      );
    }
    return result;
  }, [quotations, filterStatus, searchTerm]);

  if (!moduleEnabled) return <ModuleDisabledScreen />;

  return (
    <div className="space-y-4 p-2 sm:p-4" data-testid="quotations-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-foreground flex items-center gap-2">
            <FileText className="w-6 h-6 text-teal-600" />
            Quotations
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-muted-foreground mt-1">
            Create, share and convert quotations into bookings
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/invoices', { state: { openSettings: true } })}
            data-testid="quotation-pdf-settings-btn"
            title="Configure PDF theme, company branding, signature & T&Cs (shared with Invoices)"
          >
            <Settings className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">PDF Settings</span>
          </Button>
          <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-quotations-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={openCreate} className="bg-teal-600 hover:bg-teal-700 flex-1 sm:flex-none" data-testid="create-quotation-btn">
            <Plus className="w-4 h-4 mr-1" /> New Quotation
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-3 flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search by number, client, venue..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="quotations-search"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-44" data-testid="quotations-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* List */}
      <Card className="border-0 shadow-md">
        <CardHeader className="border-b dark:border-border bg-gradient-to-r from-slate-50 to-gray-50 dark:from-secondary dark:to-muted p-3 sm:p-4">
          <CardTitle className="text-base sm:text-lg dark:text-foreground">
            Quotations ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-2 sm:p-4">
          {loading ? (
            <div className="text-center py-8 text-gray-400"><RefreshCw className="w-6 h-6 mx-auto animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-gray-500 dark:text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No quotations yet. Create your first one!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((q) => (
                <div
                  key={q.id}
                  className="border dark:border-border rounded-lg p-3 hover:shadow-md transition-shadow bg-white dark:bg-card"
                  data-testid={`quotation-row-${q.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm sm:text-base text-gray-900 dark:text-foreground">{q.quotation_number}</span>
                        <Badge className={STATUS_COLORS[q.status] || STATUS_COLORS.draft}>{q.status}</Badge>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 truncate mt-0.5">{q.client_name} • {q.client_phone}</p>
                      <p className="text-xs text-gray-500 dark:text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Building2 className="w-3 h-3" /> {q.venue_name || '-'}
                        {q.event_date_from && <span className="ml-2">• {formatDate(q.event_date_from)}</span>}
                      </p>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-3">
                      <div className="text-right">
                        <p className="text-xs text-gray-500 dark:text-muted-foreground">Grand Total</p>
                        <p className="font-bold text-teal-600 dark:text-teal-400">{formatCurrency(q.grand_total)}</p>
                      </div>
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => { setViewTarget(q); setViewOpen(true); }}
                        data-testid={`view-quotation-${q.id}`}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="mt-2 flex flex-wrap gap-1.5 pt-2 border-t dark:border-border">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openEdit(q)} data-testid={`edit-quotation-${q.id}`}>
                      <Edit className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleDownloadPDF(q)} data-testid={`pdf-quotation-${q.id}`}>
                      <Download className="w-3 h-3 mr-1" /> PDF
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs text-green-600 border-green-300" onClick={() => handleWhatsApp(q)} data-testid={`whatsapp-quotation-${q.id}`}>
                      <MessageCircle className="w-3 h-3 mr-1" /> WhatsApp
                    </Button>
                    {q.status === 'draft' && (
                      <Button size="sm" variant="outline" className="h-7 text-xs text-blue-600 border-blue-300" onClick={() => handleAction('send', q)} data-testid={`send-quotation-${q.id}`}>
                        <Send className="w-3 h-3 mr-1" /> Mark Sent
                      </Button>
                    )}
                    {['sent', 'viewed', 'draft'].includes(q.status) && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 text-xs text-green-600 border-green-300" onClick={() => handleAction('accept', q)} data-testid={`accept-quotation-${q.id}`}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Accept
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-300" onClick={() => handleAction('reject', q)} data-testid={`reject-quotation-${q.id}`}>
                          <XCircle className="w-3 h-3 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {q.status === 'accepted' && (
                      <Button size="sm" variant="outline" className="h-7 text-xs text-purple-600 border-purple-300" onClick={() => handleAction('convert', q)} data-testid={`convert-quotation-${q.id}`}>
                        <Repeat className="w-3 h-3 mr-1" /> Convert to Booking
                      </Button>
                    )}
                    <Button
                      size="sm" variant="outline" className="h-7 text-xs text-red-600 border-red-300"
                      onClick={() => { setDeleteTarget(q); setDeleteOpen(true); }}
                      data-testid={`delete-quotation-${q.id}`}
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="quotation-form-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Quotation' : 'New Quotation'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Venue + Lead */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Venue *</Label>
                <Select value={form.venue_id} onValueChange={onVenueChange}>
                  <SelectTrigger data-testid="quotation-venue-select"><SelectValue placeholder="Select venue" /></SelectTrigger>
                  <SelectContent>
                    {venues.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Link Lead (auto-fills client details)</Label>
                <Select value={form.lead_id || 'none'} onValueChange={onLeadChange}>
                  <SelectTrigger data-testid="quotation-lead-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {leads.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.name} ({l.phone})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Client */}
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label>Client Name *</Label>
                <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="quotation-client-name" />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} data-testid="quotation-client-phone" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
              </div>
            </div>

            {/* Event */}
            <div className="grid sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2">
                <Label>Event Type</Label>
                <Input value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} placeholder="e.g. Wedding, Corporate" />
              </div>
              <div>
                <Label>From Date</Label>
                <Input type="date" value={form.event_date_from} onChange={(e) => setForm({ ...form, event_date_from: e.target.value })} />
              </div>
              <div>
                <Label>To Date</Label>
                <Input type="date" value={form.event_date_to} onChange={(e) => setForm({ ...form, event_date_to: e.target.value })} />
              </div>
              <div>
                <Label>Guest Count</Label>
                <Input type="number" value={form.guest_count} onChange={(e) => setForm({ ...form, guest_count: e.target.value })} />
              </div>
              <div>
                <Label>Valid Until</Label>
                <Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
              </div>
            </div>

            {/* Spaces */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Spaces / Halls</Label>
                <Button size="sm" variant="outline" onClick={addSpace} data-testid="add-space-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Space
                </Button>
              </div>
              <div className="space-y-2">
                {form.spaces.map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end border dark:border-border rounded p-2">
                    <div className="col-span-12 sm:col-span-4">
                      <Label className="text-xs">Name</Label>
                      <Input value={s.space_name} onChange={(e) => updateSpace(i, { space_name: e.target.value })} placeholder="e.g. Main Hall" data-testid={`space-name-${i}`} />
                    </div>
                    <div className="col-span-6 sm:col-span-3">
                      <Label className="text-xs">Type</Label>
                      <Input value={s.space_type} onChange={(e) => updateSpace(i, { space_type: e.target.value })} placeholder="AC/Non-AC" />
                    </div>
                    <div className="col-span-3 sm:col-span-2">
                      <Label className="text-xs">Qty</Label>
                      <Input type="number" value={s.quantity} onChange={(e) => updateSpace(i, { quantity: e.target.value })} />
                    </div>
                    <div className="col-span-6 sm:col-span-2">
                      <Label className="text-xs">Unit Price</Label>
                      <Input type="number" step="0.01" value={s.unit_price} onChange={(e) => updateSpace(i, { unit_price: e.target.value })} data-testid={`space-price-${i}`} />
                    </div>
                    <div className="col-span-3 sm:col-span-1">
                      <Button size="sm" variant="ghost" onClick={() => removeSpace(i)} disabled={form.spaces.length === 1}>
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="col-span-12 pt-1 border-t dark:border-border">
                      <Label className="text-[10px] text-gray-500 dark:text-muted-foreground uppercase tracking-wide mb-1 block">
                        Photos (max 2 per item, ATM card size on PDF)
                      </Label>
                      <ItemPhotosPicker
                        images={s.images || (s.image_url ? [{ url: s.image_url, orientation: s.image_orientation || 'landscape' }] : [])}
                        onChange={(next) => updateSpace(i, { images: next })}
                        testIdPrefix={`space-photo-${i}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Amenities */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold">Amenities / Services</Label>
                <Button size="sm" variant="outline" onClick={addAmenity} data-testid="add-amenity-btn">
                  <Plus className="w-3 h-3 mr-1" /> Add Amenity
                </Button>
              </div>
              <div className="space-y-2">
                {form.amenities.map((a, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end border dark:border-border rounded p-2">
                    <div className="col-span-12 sm:col-span-3">
                      <Label className="text-xs">Name</Label>
                      <Input value={a.amenity_name} onChange={(e) => updateAmenity(i, { amenity_name: e.target.value })} placeholder="e.g. Catering" />
                    </div>
                    <div className="col-span-6 sm:col-span-2">
                      <Label className="text-xs">Category</Label>
                      <Input value={a.category} onChange={(e) => updateAmenity(i, { category: e.target.value })} />
                    </div>
                    <div className="col-span-3 sm:col-span-1">
                      <Label className="text-xs">Qty</Label>
                      <Input type="number" value={a.quantity} onChange={(e) => updateAmenity(i, { quantity: e.target.value })} />
                    </div>
                    <div className="col-span-3 sm:col-span-1">
                      <Label className="text-xs">Unit</Label>
                      <Input value={a.unit} onChange={(e) => updateAmenity(i, { unit: e.target.value })} />
                    </div>
                    <div className="col-span-3 sm:col-span-1">
                      <Label className="text-xs">Days</Label>
                      <Input type="number" value={a.days} onChange={(e) => updateAmenity(i, { days: e.target.value })} />
                    </div>
                    <div className="col-span-6 sm:col-span-3">
                      <Label className="text-xs">Unit Price</Label>
                      <Input type="number" step="0.01" value={a.unit_price} onChange={(e) => updateAmenity(i, { unit_price: e.target.value })} />
                    </div>
                    <div className="col-span-3 sm:col-span-1">
                      <Button size="sm" variant="ghost" onClick={() => removeAmenity(i)}>
                        <X className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="col-span-12 pt-1 border-t dark:border-border">
                      <Label className="text-[10px] text-gray-500 dark:text-muted-foreground uppercase tracking-wide mb-1 block">
                        Photos (max 2 per item, ATM card size on PDF)
                      </Label>
                      <ItemPhotosPicker
                        images={a.images || (a.image_url ? [{ url: a.image_url, orientation: a.image_orientation || 'landscape' }] : [])}
                        onChange={(next) => updateAmenity(i, { images: next })}
                        testIdPrefix={`amenity-photo-${i}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Discount & GST */}
            <div className="grid sm:grid-cols-4 gap-3">
              <div>
                <Label>Discount %</Label>
                <Input type="number" step="0.01" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value, discount_amount: 0 })} data-testid="quotation-discount-percent" />
              </div>
              <div>
                <Label>or Flat Discount ₹</Label>
                <Input type="number" step="0.01" value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value, discount_percent: 0 })} />
              </div>
              <div>
                <Label>GST Type</Label>
                <Select value={form.gst_type} onValueChange={(v) => setForm({ ...form, gst_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exclusive">Exclusive</SelectItem>
                    <SelectItem value="inclusive">Inclusive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>GST %</Label>
                <Input type="number" step="0.01" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} data-testid="quotation-gst-percent" />
              </div>
            </div>

            {/* Live Totals */}
            <div className="bg-gray-50 dark:bg-secondary rounded-lg p-3 text-sm space-y-1">
              <div className="flex justify-between"><span>Subtotal</span><span>{formatCurrency(totals.subtotal)}</span></div>
              <div className="flex justify-between"><span>Discount</span><span>- {formatCurrency(totals.discount)}</span></div>
              <div className="flex justify-between"><span>GST ({form.gst_percent}% {form.gst_type})</span><span>{formatCurrency(totals.gst)}</span></div>
              <div className="flex justify-between border-t dark:border-border pt-1 mt-1 font-bold text-teal-600 dark:text-teal-400">
                <span>Grand Total</span><span data-testid="quotation-grand-total">{formatCurrency(totals.grand)}</span>
              </div>
            </div>

            {/* Terms & Notes */}
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Terms & Conditions</Label>
                <Textarea rows={3} value={form.terms_and_conditions} onChange={(e) => setForm({ ...form, terms_and_conditions: e.target.value })} />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700" data-testid="save-quotation-btn">
              {editingId ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Quotation {viewTarget?.quotation_number}</DialogTitle>
          </DialogHeader>
          {viewTarget && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Client</span>
                <span className="font-medium">{viewTarget.client_name}</span>
              </div>
              <div className="flex justify-between"><span className="text-gray-500">Phone</span><span>{viewTarget.client_phone}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Venue</span><span>{viewTarget.venue_name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Status</span><Badge className={STATUS_COLORS[viewTarget.status]}>{viewTarget.status}</Badge></div>
              <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{formatCurrency(viewTarget.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Discount</span><span>- {formatCurrency(viewTarget.discount_amount)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">GST</span><span>{formatCurrency(viewTarget.gst_amount)}</span></div>
              <div className="flex justify-between border-t pt-2 font-bold text-teal-600"><span>Grand Total</span><span>{formatCurrency(viewTarget.grand_total)}</span></div>
              {viewTarget.spaces?.length > 0 && (
                <div>
                  <p className="font-semibold mt-2">Spaces</p>
                  {viewTarget.spaces.map((s, i) => (
                    <div key={i} className="flex justify-between text-xs py-0.5">
                      <span>{s.space_name} ({s.space_type}) x {s.quantity}</span>
                      <span>{formatCurrency(s.total_price)}</span>
                    </div>
                  ))}
                </div>
              )}
              {viewTarget.amenities?.length > 0 && (
                <div>
                  <p className="font-semibold mt-2">Amenities</p>
                  {viewTarget.amenities.map((a, i) => (
                    <div key={i} className="flex justify-between text-xs py-0.5">
                      <span>{a.amenity_name} x {a.quantity} {a.unit} x {a.days}d</span>
                      <span>{formatCurrency(a.total_price)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={handleDelete}
        title="Delete Quotation"
        description={`Are you sure you want to delete ${deleteTarget?.quotation_number}? This cannot be undone.`}
      />
    </div>
  );
};

export default Quotations;
